<template>
  <UiButton
    :aria-label="t('ariaLabels.openNavigation')"
    appearance="ghost"
    data-testid="mobileMenuTrigger"
    size="sm"
    @click="handleMenuIconClick">
    <UiIcon name="menu" />
  </UiButton>
  <Teleport to="body">
    <Transition :duration="300" name="navigation">
      <div
        v-if="menuIsOpen"
        :data-is-open="menuIsOpen"
        class="max-w-full"
        data-testid="mobileMenu">
        <div
          class="fixed inset-0 z-99 bg-neutral-primary/70"
          data-testid="mobileNavBg"
          @click="menuIsOpen = false"></div>
        <div
          class="fixed top-0 left-0 z-100 grid h-full w-11/12 max-w-80 grid-rows-[min-content_1fr_min-content] bg-surface-level-1"
          data-testid="mobileNav">
          <div class="pt-4 pr-4 pl-2">
            <UiButton
              :aria-label="t('ariaLabels.closeNavigation')"
              appearance="ghost"
              size="sm"
              @click="handleCloseClick">
              <UiIcon name="close" />
            </UiButton>
          </div>
          <ul class="divide-y divide-neutral-faint p-4">
            <li
              v-for="item in navigationItems"
              :key="item.label"
              data-testid="mobileNavItem">
              <AppMenuItem
                :item="item"
                active-class="text-primary"
                item-class="inline-block py-4 text-sm font-semibold"
                @item-clicked="handleItemClick(item)" />
            </li>
          </ul>
          <div class="p-4">
            <AppMenuFeedbackButton
              @item-clicked="
                trackEvent(
                  'mobileNavigation_click_button_feedback',
                  `${t('navigation.feedback')}/mailto:dpa-news-hub@dpa.com`,
                )
              " />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiIcon from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import type { NavigationItem } from "@/composition/shared/useNavigation";
import useNavigation from "@/composition/shared/useNavigation.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";

import AppMenuFeedbackButton from "./AppMenuFeedbackButton.vue";
import AppMenuItem from "./AppMenuItem.vue";

const { t } = useI18n();
const { trackEvent } = useTracking();
const { navigationItems } = useNavigation();

function handleMenuIconClick() {
  menuIsOpen.value = true;
  trackEvent(
    "mobileNavigation_click_iconButton_toggleMobileNavigation",
    `menu / open`,
  );
}

function handleCloseClick() {
  menuIsOpen.value = false;
  trackEvent(
    "mobileNavigation_click_iconButton_toggleMobileNavigation",
    `menu / close`,
  );
}
function handleItemClick(item: NavigationItem) {
  trackEvent("mobileNavigation_click_textButton_navlink", item.url.fullPath);
  menuIsOpen.value = false;
}

const menuIsOpen = ref(false);
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.navigation-enter-active [data-testid="mobileNavBg"] {
  animation: fadeIn 200ms ease both;
}

.navigation-leave-active [data-testid="mobileNavBg"] {
  animation: fadeOut 200ms ease both;
}

.navigation-enter-active [data-testid="mobileNav"],
.navigation-leave-active [data-testid="mobileNav"] {
  transition: transform 300ms cubic-bezier(0.05, 0.7, 0.1, 1);
}

.navigation-enter-from [data-testid="mobileNav"] {
  transform: translateX(-100%);
}

.navigation-leave-to [data-testid="mobileNav"] {
  transform: translateX(-100%);
  transition-timing-function: cubic-bezier(0.3, 0, 0.8, 0.15);
}
</style>
